import React, { useEffect, useRef, useState } from "react";
import { Input, InputRef, message } from "antd";

interface TimeRangeInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const pad = (val: string) => val.padStart(2, "0");

const formatTime = (str: string): string => {
  const digits = str.replace(/[^\d]/g, "");
  const parts = digits.match(/.{1,2}/g) || [];

  const [hh = "00", mm = "00", ss = "00"] = parts;

  const hour = Math.min(Number(hh) || 0, 23);
  const minute = Math.min(Number(mm) || 0, 59);
  const second = Math.min(Number(ss) || 0, 59);

  return `${pad(hour.toString())}:${pad(minute.toString())}:${pad(
    second.toString()
  )}`;
};

const formatWithSeparators = (input: string) => {
  const digits = input.replace(/[^\d]/g, "");
  if (digits.length > 12) return input;

  let formatted = "";
  for (let i = 0; i < digits.length; i++) {
    formatted += digits[i];
    if (i === 1 || i === 3) formatted += ":";
    if (i === 5) formatted += " - ";
    if (i === 7 || i === 9) formatted += ":";
  }

  return formatted;
};

const TimeRangeInput: React.FC<TimeRangeInputProps> = ({
  value = "",
  onChange,
  disabled = false,
  placeholder,
}) => {
  const [input, setInput] = useState(value);
  const [error, setError] = useState(false);
  const inputRef = useRef<InputRef>(null);

  

  useEffect(() => {
    setInput(value || "");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (/[^0-9:-\s]/.test(raw)) {
      message.error("Faqat raqamlar, ':' va '-' belgisi kiritilishi mumkin");
      return;
    }

    const isDeleting = input.length > raw.length;

    if (isDeleting) {
      setInput(raw);
      onChange?.(raw);
    } else {
      const inputEl = inputRef.current?.input;
      const cursorPos = inputEl?.selectionStart || raw.length;

      const formatted = formatWithSeparators(raw);
      setInput(formatted);
      onChange?.(formatted);

      setTimeout(() => {
        if (inputEl) {
          const newPos = cursorPos + (formatted.length - raw.length);
          inputEl.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }
  };

  const parseTimeToSeconds = (time: string): number => {
    const [h = "0", m = "0", s = "0"] = time.split(":");
    return parseInt(h) * 3600 + parseInt(m) * 60 + parseInt(s);
  };

  const handleBlur = () => {
    if (!input?.trim()) return;

    const sections = input.split("-").map((s) => s.trim());

    const start = formatTime(sections[0] || "");
    const end = formatTime(sections[1] || "");

    const parsed = `${start} - ${end}`;

    setInput(parsed);
    onChange?.(parsed);

    const startSec = parseTimeToSeconds(start);
    const endSec = parseTimeToSeconds(end);

    if (startSec >= endSec) {
      message.error("Boshlanish vaqti tugash vaqtidan kichik bo'lishi kerak");
      setError(true);
    } else {
      setError(false);
    }
  };

  return (
    <Input
      ref={inputRef}
      value={input}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder || "00:00:00 - 00:00:00"}
      allowClear
      maxLength={19}
      status={error ? "error" : ""}
    />
  );
};

export default TimeRangeInput;
